import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TrendChart } from '../../dashboard/components';
import DashboardKPICards from '../../dashboard/components/DashboardKPICards';
import AnalyticsPanel from '../../dashboard/components/AnalyticsPanel';
import { useDashboardData } from '../../dashboard/hooks/useDashboardData';
import type { TimeRangeType } from '../../dashboard/hooks/useDashboardData';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './EnterpriseContentPage.module.css';

export default function EnterpriseChartsPage() {
  const { token } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTimeRange: TimeRangeType = '30D';
  const selectedJurisdiction = 'All';
  const selectedCountry = '';
  const selectedContinent = 'All';
  const activeFeedTab = 'dashboard';

  const getDateRangeForAPI = useCallback(() => {
    const ranges: Partial<Record<TimeRangeType, { days: number }>> = {
      '24H': { days: 1 },
      '7D': { days: 7 },
      '30D': { days: 30 },
      '90D': { days: 90 },
      '1Y': { days: 365 },
    };

    return ranges[selectedTimeRange] ?? {};
  }, [selectedTimeRange]);

  const {
    totalJurisdictions,
    totalRegulations,
    countriesTracked,
    supranationalEntries,
    jurisdictionStats,
    countryStats,
    trendData,
    isLoadingTrends,
  } = useDashboardData(token, {
    selectedJurisdiction,
    selectedTimeRange,
    dateFilterType: 'last_action_date',
    customStartDate: '',
    customEndDate: '',
    selectedCountry,
    selectedContinent,
    activeFeedTab,
    getDateRangeForAPI,
  });

  const regulationsRoute = location.pathname.startsWith('/admin/dashboard')
    ? '/admin/dashboard/regulations-breakdown'
    : location.pathname.startsWith('/startup/dashboard')
      ? '/startup/dashboard/regulations-breakdown'
      : location.pathname.startsWith('/government/dashboard')
        ? '/government/dashboard/regulations-breakdown'
        : location.pathname.startsWith('/enterprise/dashboard')
          ? '/enterprise/dashboard/regulations-breakdown'
          : '/dashboard/regulations-breakdown';

  return (
    <div className={styles.page}>
      <Eyebrow tone="default" dot>ANALYTICS</Eyebrow>
      <PageHeading
        accentWord="Statistics"
        accentTone="orange"
        size="h2"
        align="left"
        level="h2"
        className={styles.title}
      >
        Charts &amp; Statistics
      </PageHeading>
      <DashboardKPICards
        totalJurisdictions={totalJurisdictions}
        totalRegulations={totalRegulations}
        countriesTracked={countriesTracked}
        supranationalEntries={supranationalEntries}
        onActivePoliciesClick={() => navigate(regulationsRoute)}
      />
      <div className={styles.chartRow}>
        <div style={{ marginBottom: 18 }}><AnalyticsPanel token={token} theme={theme} /></div>
        <TrendChart
          data={trendData}
          isLoading={isLoadingTrends}
          totalRegulations={totalRegulations ?? undefined}
          jurisdictionStats={jurisdictionStats}
          countryStats={countryStats}
          hideBreakdown
        />
      </div>
    </div>
  );
}
